import { Document, Page, View, Text, Image } from '@react-pdf/renderer';
import { styles, BRAND_COLOR, BORDER_COLOR } from './styles';
import HtmlRenderer from './HtmlRenderer';
import { moneyFmt, rowTotalConVariaciones, aplicarVariaciones, aplicarOferta, etiquetaOferta, calcularMontosPlanPago } from '../../utils/helpers';

// =====================================================
// Helpers locales
// =====================================================
const formatearFecha = (fecha) => {
    if (!fecha) return '';
    if (typeof fecha === 'string' && fecha.match(/^\d{4}-\d{2}-\d{2}/)) {
        const [anio, mes, dia] = fecha.split('T')[0].split('-');
        return `${dia}/${mes}/${anio}`;
    }
    const d = new Date(fecha);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const anio = d.getFullYear();
    return `${dia}/${mes}/${anio}`;
};

// Agrupar servicios por categoria. Separa el monto del servicio principal
// del de los costos fijos, asi cada CF se muestra como sub-fila con su importe.
//
// IMPORTANTE: calcula el totalItem al vuelo aplicando TODA la cadena de
// variaciones (fila → bloque → servicio). No confiar en `item.total` del state
// porque puede estar desactualizado si el user aplico variaciones despues del
// ultimo save (los handlers de asignaciones/costos si recalculan `s.total`, pero
// aplicarCambiosVariaciones NO — para no mutar el objeto y evitar re-renders).
const agruparPorCategoria = (servicios, tiposServicio) => {
    const grupos = {};
    servicios.forEach((item) => {
        const tipo = tiposServicio.find(t => t.id === item.tipo_servicio_id);
        const categoria = tipo?.categoria_nombre || 'Otros Servicios';
        if (!grupos[categoria]) grupos[categoria] = [];

        // CF: total efectivo con variaciones + oferta comercial (si aplica)
        const cfs = (item.costosFijos || []).map(cf => {
            const totalPre = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
            return {
                nombre: cf.costo_fijo_nombre || 'Costo fijo',
                descripcion: cf.descripcion || '',
                periodoNombre: cf.periodo_nombre || null,
                subtotal: aplicarOferta(totalPre, cf.oferta),
                subtotalPreOferta: totalPre,
                oferta: cf.oferta || null
            };
        });
        const sumaCfs = cfs.reduce((acc, c) => acc + c.subtotal, 0);

        // Total FINAL del servicio con TODAS las variaciones + ofertas por fila
        const totalFilasPers = (item.asignaciones || []).reduce((acc, a) => {
            const rent = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0.68;
            const tPre = rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            return acc + aplicarOferta(tPre, a.oferta);
        }, 0);
        const totalBloquePers = aplicarVariaciones(totalFilasPers, item.variaciones_bloque_personal || []);
        // Los CFs ya vienen con oferta aplicada en `cfs[].subtotal`
        const totalFilasCf = sumaCfs;
        const totalBloqueCf = aplicarVariaciones(totalFilasCf, item.variaciones_bloque_costos || []);
        const baseServ = totalBloquePers + totalBloqueCf;
        const totalItemPreOferta = aplicarVariaciones(baseServ, item.variaciones || []);
        // Oferta comercial a nivel servicio entero (opcional)
        const totalItem = aplicarOferta(totalItemPreOferta, item.oferta);

        // Cuando hay personal: el header muestra el "servicio puro" (personal +
        // variaciones) y los CF se listan aparte con sus subtotales.
        // Cuando el servicio SOLO tiene CF (sin personal): mostramos el total
        // completo en el header y ocultamos los subtotales individuales de CF
        // para no duplicar el monto en pantalla — los CF quedan como detalle
        // (nombre + descripcion) del servicio.
        const soloCostosFijos = totalFilasPers === 0 && cfs.length > 0;
        // El "totalServicio" mostrado en el header. Si el servicio tiene oferta,
        // el pre-oferta se calcula de la misma forma (restando CFs para el caso
        // con personal) para poder mostrar el tachado consistente.
        const totalServicio = soloCostosFijos
            ? totalItem
            : Math.max(0, totalItem - sumaCfs);
        const totalServicioPreOferta = item.oferta
            ? (soloCostosFijos ? totalItemPreOferta : Math.max(0, totalItemPreOferta - sumaCfs))
            : totalServicio;

        grupos[categoria].push({
            // El titulo es el encabezado del servicio en el PDF (obligatorio en el form).
            // Si por algun motivo esta vacio, caemos al tipo_servicio_nombre.
            nombre: (item.titulo && String(item.titulo).trim())
                || item.tipo_servicio_nombre
                || tipo?.nombre
                || 'Servicio',
            detalle: item.detalle || '',
            totalServicio,
            totalServicioPreOferta,
            oferta: item.oferta || null,
            costosFijos: cfs,
            soloCostosFijos,
            periodoNombre: item.periodo_nombre || null
        });
    });
    return grupos;
};

// =====================================================
// Sub-componentes
// =====================================================

const Header = ({ cotizacion, configuracion, logoDataUrl }) => (
    <>
        <View style={styles.header}>
            {logoDataUrl && (
                <Image src={logoDataUrl} style={styles.headerLogo} />
            )}
            <Text style={styles.headerTitle}>
                COTIZACIÓN DE SERVICIOS - {cotizacion.numero}
            </Text>
        </View>
        <View style={styles.infoRow}>
            <View style={styles.infoCol}>
                <Text style={styles.infoLine}>
                    {configuracion.empresa_nombre || 'Alma Industria Creativa E.I.R.L.'}
                </Text>
                {configuracion.empresa_ruc && (
                    <Text style={styles.infoLine}>RUC: {configuracion.empresa_ruc}</Text>
                )}
                {(configuracion.empresa_email || configuracion.empresa_telefono) && (
                    <Text style={styles.infoLine}>
                        {[configuracion.empresa_email, configuracion.empresa_telefono]
                            .filter(Boolean)
                            .join(' | ')}
                    </Text>
                )}
            </View>
            <View style={[styles.infoCol, styles.infoColRight]}>
                <Text style={styles.infoLine}>Fecha: {formatearFecha(cotizacion.fecha)}</Text>
                <Text style={styles.infoLine}>Cliente: {cotizacion.cliente_nombre}</Text>
                {cotizacion.cliente_empresa && (
                    <Text style={styles.infoLine}>Empresa: {cotizacion.cliente_empresa}</Text>
                )}
                {cotizacion.tiempo_desarrollo && (
                    <Text style={styles.infoLine}>Tiempo: {cotizacion.tiempo_desarrollo}</Text>
                )}
                <Text style={styles.infoLine}>Moneda: {cotizacion.moneda || 'PEN'}</Text>
                <Text style={styles.infoLine}>Validez: {cotizacion.validez} días</Text>
            </View>
        </View>
    </>
);

const TablaServiciosPorCategoria = ({ categoria, items, currency }) => (
    <View wrap={true}>
        <Text style={styles.categoriaTitle}>{categoria}</Text>

        {/* Header de tabla */}
        <View style={styles.tableHead} fixed={false}>
            <View style={{ width: 420 }}>
                <Text style={styles.tableHeadCell}>Servicio</Text>
            </View>
            <View style={{ width: 90 }}>
                <Text style={[styles.tableHeadCell, styles.tableHeadCellRight]}>Importe</Text>
            </View>
        </View>

        {/* Filas: servicio principal + costos fijos como sub-filas indentadas.
            Estrategia contra overlap:
              - Header (titulo + importe) es un View wrap=false → NUNCA se separa
                del cierre inferior. Si no cabe salta a la siguiente pagina entero.
              - Body (detalle HTML largo) es un View wrap=true → fluye libremente
                entre paginas (los chunks aparecen abajo del titulo, siguen en la
                pagina siguiente si no caben).
              - CFs sub-filas: cada una wrap=false individual, fluyen entre paginas. */}
        {items.map((item, idx) => (
            <View key={idx} wrap={true}>
                {/* Header atomico: titulo + importe (no se separan) */}
                <View wrap={false} style={item.detalle ? styles.tableRowHeader : styles.tableRow}>
                    <View style={item.detalle ? styles.tableCellServicioHeader : styles.tableCellServicio}>
                        <Text style={styles.servicioNombre}>{item.nombre}</Text>
                    </View>
                    <View style={styles.tableCellImporte}>
                        {item.oferta && (
                            <Text style={styles.ofertaPrecioTachado}>
                                {moneyFmt(item.totalServicioPreOferta, currency)}
                            </Text>
                        )}
                        <Text style={item.oferta ? styles.ofertaPrecioFinal : null}>
                            {moneyFmt(item.totalServicio, currency)}
                        </Text>
                        {item.periodoNombre && (
                            <Text style={styles.servicioPeriodoSuffix}>/ {String(item.periodoNombre).toLowerCase()}</Text>
                        )}
                        {item.oferta && (
                            <Text style={styles.ofertaMotivo}>
                                {item.oferta.motivo}
                            </Text>
                        )}
                    </View>
                </View>

                {/* Body: detalle largo — puede fluir entre paginas.
                    Si no hay detalle, no se renderiza (el header ya cierra el row). */}
                {item.detalle && (
                    <View style={styles.tableRowBody}>
                        <View style={styles.tableCellServicioBody}>
                            <HtmlRenderer html={item.detalle} />
                        </View>
                        <View style={styles.tableCellImporteEmpty} />
                    </View>
                )}

                {/* Sub-filas de costos fijos del servicio (indentadas). */}
                {item.costosFijos.map((cf, cfIdx) => (
                    <View key={`cf-${cfIdx}`} style={styles.costoFijoRow} wrap={false}>
                        <View style={styles.costoFijoCellNombre}>
                            <Text style={styles.costoFijoNombre}>{cf.nombre}</Text>
                            {cf.descripcion && (
                                <Text style={styles.costoFijoDescripcion}>{cf.descripcion}</Text>
                            )}
                        </View>
                        <View style={styles.tableCellImporte}>
                            {cf.oferta && (
                                <Text style={styles.ofertaPrecioTachadoCf}>
                                    {moneyFmt(cf.subtotalPreOferta, currency)}
                                </Text>
                            )}
                            <Text style={cf.oferta ? [styles.costoFijoImporte, styles.ofertaPrecioFinal] : styles.costoFijoImporte}>
                                {moneyFmt(cf.subtotal, currency)}
                            </Text>
                            {cf.periodoNombre && (
                                <Text style={styles.servicioPeriodoSuffix}>/ {String(cf.periodoNombre).toLowerCase()}</Text>
                            )}
                            {cf.oferta && (
                                <Text style={styles.ofertaMotivo}>
                                    {cf.oferta.motivo}
                                </Text>
                            )}
                        </View>
                    </View>
                ))}
            </View>
        ))}

        <View style={styles.categoriaGap} />
    </View>
);

const Extras = ({ extras, currency }) => {
    if (!extras || extras.length === 0) return null;
    const subtotalExtras = extras.reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
    return (
        <View wrap={false}>
            <Text style={styles.categoriaTitle}>Extras</Text>
            <View style={styles.tableHead}>
                <View style={{ width: 420 }}>
                    <Text style={styles.tableHeadCell}>Descripción</Text>
                </View>
                <View style={{ width: 90 }}>
                    <Text style={[styles.tableHeadCell, styles.tableHeadCellRight]}>Importe</Text>
                </View>
            </View>
            {extras.map((extra, idx) => (
                <View key={idx} style={styles.tableRow}>
                    <View style={styles.tableCellServicio}>
                        {extra.detalle
                            ? <HtmlRenderer html={extra.detalle} />
                            : <Text>Extra</Text>}
                    </View>
                    <View style={styles.tableCellImporte}>
                        <Text>{moneyFmt(parseFloat(extra.costo) || 0, currency)}</Text>
                    </View>
                </View>
            ))}
            <View style={styles.tableRowSubtotal}>
                <View style={styles.tableCellSubtotalLabel}>
                    <Text>Subtotal extras</Text>
                </View>
                <View style={styles.tableCellSubtotalValue}>
                    <Text>{moneyFmt(subtotalExtras, currency)}</Text>
                </View>
            </View>
            <View style={styles.categoriaGap} />
        </View>
    );
};

// Extrae los items (servicios + CFs) con recurrencia. Requiere solo periodo_nombre
// (los "Único" o sin nombre se excluyen). No requiere meses — no calculamos totales.
const esPeriodoRecurrente = (nombre) => {
    if (!nombre) return false;
    const n = String(nombre).toLowerCase().trim();
    return n !== 'unico' && n !== 'único' && n !== 'ninguno';
};

const extraerRecurrentes = (servicios) => {
    const items = [];
    (servicios || []).forEach(s => {
        // Servicio con periodo recurrente: se toma el TOTAL COMPLETO del servicio
        // (personal + CFs sin periodo propio + variaciones + oferta). Los CFs
        // con periodo propio se listan aparte, asi que se restan aca para no duplicar.
        if (esPeriodoRecurrente(s.periodo_nombre)) {
            const totalFilasPers = (s.asignaciones || []).reduce((acc, a) => {
                const rent = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0.68;
                const tPre = rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
                return acc + aplicarOferta(tPre, a.oferta);
            }, 0);
            const totalBloquePers = aplicarVariaciones(totalFilasPers, s.variaciones_bloque_personal || []);
            const totalFilasCf = (s.costosFijos || []).reduce((acc, cf) => {
                const tCf = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                return acc + aplicarOferta(tCf, cf.oferta);
            }, 0);
            const totalBloqueCf = aplicarVariaciones(totalFilasCf, s.variaciones_bloque_costos || []);
            const totalPreOferta = aplicarVariaciones(totalBloquePers + totalBloqueCf, s.variaciones || []);
            const totalItem = aplicarOferta(totalPreOferta, s.oferta);

            // Restar CFs con periodo propio (se listan como items independientes).
            // Ya viene con oferta aplicada por CF.
            const sumaCfsConPeriodo = (s.costosFijos || []).reduce((acc, cf) => {
                if (!esPeriodoRecurrente(cf.periodo_nombre)) return acc;
                const tCf = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                return acc + aplicarOferta(tCf, cf.oferta);
            }, 0);
            const montoServ = Math.max(0, totalItem - sumaCfsConPeriodo);

            if (montoServ > 0) {
                items.push({
                    nombre: (s.titulo && String(s.titulo).trim()) || s.tipo_servicio_nombre || 'Servicio',
                    monto: montoServ,
                    periodoNombre: s.periodo_nombre
                });
            }
        }
        // Cada CF con periodo (snapshot o heredado del catalogo). Aplicamos oferta.
        (s.costosFijos || []).forEach(cf => {
            if (esPeriodoRecurrente(cf.periodo_nombre)) {
                const totPre = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                const subtotal = aplicarOferta(totPre, cf.oferta);
                if (subtotal > 0) {
                    items.push({
                        nombre: cf.costo_fijo_nombre || 'Costo fijo',
                        monto: subtotal,
                        periodoNombre: cf.periodo_nombre
                    });
                }
            }
        });
    });
    return items;
};

// Bloque a la izquierda con recurrentes agrupados por periodo (informativo).
const PagosRecurrentes = ({ servicios, currency }) => {
    const items = extraerRecurrentes(servicios);
    if (items.length === 0) return null;

    const totalesPorPeriodo = items.reduce((acc, it) => {
        const key = String(it.periodoNombre).toLowerCase();
        acc[key] = (acc[key] || 0) + it.monto;
        return acc;
    }, {});

    return (
        <View style={styles.pagosRecWrap} wrap={false}>
            <Text style={styles.pagosRecTitle}>Pagos Recurrentes</Text>
            <Text style={styles.pagosRecDesc}>
                Los pagos recurrentes se deberán renovar según el período indicado para mantener el servicio activo.
            </Text>
            <View style={styles.pagosRecTable}>
                {items.map((it, i) => (
                    <View key={i} style={styles.pagosRecRow}>
                        <Text style={styles.pagosRecLabel}>{it.nombre}</Text>
                        <Text style={styles.pagosRecValue}>
                            {moneyFmt(it.monto, currency)}
                            <Text style={styles.pagosRecPeriodo}> / {String(it.periodoNombre).toLowerCase()}</Text>
                        </Text>
                    </View>
                ))}
                {Object.entries(totalesPorPeriodo).map(([periodo, total], i) => (
                    <View key={`total-${periodo}`} style={[styles.pagosRecRow, i === 0 ? styles.pagosRecRowTotal : null]}>
                        <Text style={styles.pagosRecLabelBold}>Total {periodo}</Text>
                        <Text style={styles.pagosRecValueBold}>{moneyFmt(total, currency)}</Text>
                    </View>
                ))}
            </View>
        </View>
    );
};

// Agrupa TODOS los items del pago (servicios normales + CFs con periodo propio +
// extras) por tipo de periodo. Los servicios sin recurrencia van a "unico".
// Devuelve { grupos, subtotales } listo para renderizar el bloque de totales.
const agruparItemsPorPeriodo = (servicios, extras) => {
    const grupos = {}; // { 'unico': [...], 'mensual': [...], 'anual': [...] }
    const pushItem = (periodoRaw, item) => {
        const per = String(periodoRaw || 'unico').toLowerCase().trim();
        const key = (per === 'único' || per === 'ninguno' || per === '') ? 'unico' : per;
        if (!grupos[key]) grupos[key] = [];
        grupos[key].push(item);
    };

    (servicios || []).forEach(s => {
        if (s.es_opcional) return; // opcionales viven en su propio bloque al final

        // Personal: totales pre-oferta y con oferta (para sumar descuentos)
        let totalFilasPersPre = 0;
        let totalFilasPers = 0;
        (s.asignaciones || []).forEach(a => {
            const rent = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0.68;
            const tPre = rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            totalFilasPersPre += tPre;
            totalFilasPers += aplicarOferta(tPre, a.oferta);
        });
        const totalBloquePersPre = aplicarVariaciones(totalFilasPersPre, s.variaciones_bloque_personal || []);
        const totalBloquePers = aplicarVariaciones(totalFilasPers, s.variaciones_bloque_personal || []);

        // CFs: pre-oferta y con oferta
        let totalFilasCfPre = 0;
        let totalFilasCf = 0;
        (s.costosFijos || []).forEach(cf => {
            const tCf = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
            totalFilasCfPre += tCf;
            totalFilasCf += aplicarOferta(tCf, cf.oferta);
        });
        const totalBloqueCfPre = aplicarVariaciones(totalFilasCfPre, s.variaciones_bloque_costos || []);
        const totalBloqueCf = aplicarVariaciones(totalFilasCf, s.variaciones_bloque_costos || []);

        const totalServicioPreOferta = aplicarVariaciones(totalBloquePersPre + totalBloqueCfPre, s.variaciones || []);
        const totalServicioSoloAsigCfOferta = aplicarVariaciones(totalBloquePers + totalBloqueCf, s.variaciones || []);
        const totalServicio = aplicarOferta(totalServicioSoloAsigCfOferta, s.oferta);

        // Proporción que deja la oferta del servicio; se traslada a los CFs con periodo propio sin oferta propia
        const factorOfertaServicio = totalServicioSoloAsigCfOferta > 0 ? totalServicio / totalServicioSoloAsigCfOferta : 1;

        // CFs con periodo propio se listan aparte — restar tanto en pre como con oferta.
        const cfsConPeriodo = (s.costosFijos || [])
            .filter(cf => esPeriodoRecurrente(cf.periodo_nombre))
            .map(cf => {
                const totPre = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                const monto = cf.oferta && cf.oferta.modalidad ? aplicarOferta(totPre, cf.oferta) : totPre * factorOfertaServicio;
                return { cf, totPre, monto };
            });
        const sumaCfsConPeriodoPre = cfsConPeriodo.reduce((acc, c) => acc + c.totPre, 0);
        const sumaCfsConPeriodo = cfsConPeriodo.reduce((acc, c) => acc + c.monto, 0);
        const montoServicio = Math.max(0, totalServicio - sumaCfsConPeriodo);
        const montoServicioPre = Math.max(0, totalServicioPreOferta - sumaCfsConPeriodoPre);

        if (montoServicio > 0 || montoServicioPre > 0) {
            pushItem(s.periodo_nombre, {
                nombre: (s.titulo && String(s.titulo).trim()) || s.tipo_servicio_nombre || 'Servicio',
                monto: montoServicio,
                montoPre: montoServicioPre,
                periodoNombre: s.periodo_nombre || null
            });
        }

        // CFs con periodo propio como items independientes, con su oferta o la del servicio.
        cfsConPeriodo.forEach(({ cf, totPre, monto }) => {
            if (monto > 0 || totPre > 0) {
                pushItem(cf.periodo_nombre, {
                    nombre: cf.costo_fijo_nombre || 'Costo fijo',
                    monto,
                    montoPre: totPre,
                    periodoNombre: cf.periodo_nombre
                });
            }
        });
    });

    // Los extras van a su propio grupo: no son un periodo, no entran en la base
    // del IGV y conviene que el cliente los vea sumados aparte.
    (extras || []).forEach(e => {
        const monto = parseFloat(e.costo) || 0;
        if (monto > 0) {
            pushItem('extras', {
                nombre: (e.detalle ? String(e.detalle).replace(/<[^>]+>/g, '').trim() : '') || 'Extra',
                monto,
                montoPre: monto,
                periodoNombre: null
            });
        }
    });

    // Subtotales por grupo (con oferta) + descuento total (pre - con oferta).
    const subtotales = {};
    let descuentoTotal = 0;
    Object.entries(grupos).forEach(([per, items]) => {
        subtotales[per] = items.reduce((acc, it) => acc + it.monto, 0);
        descuentoTotal += items.reduce((acc, it) => acc + Math.max(0, (it.montoPre || 0) - it.monto), 0);
    });

    return { grupos, subtotales, descuentoTotal };
};

// Etiqueta legible del periodo para el titulo de la seccion.
// 'extras' no es un periodo: es un grupo aparte que no se renueva
const esGrupoRecurrente = (per) => per !== 'unico' && per !== 'extras';

const tituloPeriodo = (per) => {
    if (per === 'unico') return 'Pago único';
    if (per === 'extras') return 'Extras';
    // Capitalizar primera letra: "mensual" → "Mensual" → "Pagos mensuales"
    return `Pagos ${per}es`.replace(/ses$/, 'es'); // "mensual" → "Pagos mensuales", "anual" → "Pagos anuales"
};

const labelSubtotal = (per) => {
    if (per === 'unico') return 'Subtotal pago único';
    if (per === 'extras') return 'Subtotal extras';
    return `Subtotal ${per}`;
};

// Nuevo componente: agrupa TODO (pagos unicos + recurrentes) con subtotales
// por periodo, IGV y TOTAL final. Reemplaza a Totales + PagosRecurrentes.
const TotalesConDesglose = ({ servicios, extras, cotizacion, igvRate, currency }) => {
    const { grupos, subtotales, descuentoTotal } = agruparItemsPorPeriodo(servicios, extras);

    // Orden: primero 'unico', luego mensual, luego anual, luego resto alfabetico.
    const ordenPeriodo = (per) => {
        if (per === 'unico') return 0;
        if (per === 'mensual') return 1;
        if (per === 'anual') return 2;
        if (per === 'extras') return 9; // siempre al final: no es un periodo
        return 3;
    };
    const periodosOrdenados = Object.keys(grupos).sort(
        (a, b) => ordenPeriodo(a) - ordenPeriodo(b) || a.localeCompare(b)
    );

    const subtotalTotal = Object.values(subtotales).reduce((a, b) => a + b, 0);
    // Los extras NO forman parte de la base del IGV — se suman DESPUES del IGV
    // (mismo criterio que el form). Los descontamos del subtotal para el calculo
    // del IGV; siguen apareciendo en el desglose por periodo dentro de "unico".
    const subtotalExtras = (extras || []).reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
    const baseIgv = Math.max(0, subtotalTotal - subtotalExtras);
    const aplicaIgv = cotizacion.incluye_igv !== false && cotizacion.incluye_igv !== 0;
    const igv = aplicaIgv ? baseIgv * igvRate : 0;
    const total = subtotalTotal + igv;

    return (
        <View style={styles.tdWrap}>
            {/* Listado de items agrupados por periodo — todos los grupos, incluso
                "unico", llevan titulo de seccion para consistencia visual. */}
            {periodosOrdenados.map((per) => (
                <View key={per} style={styles.tdGrupo} wrap={false}>
                    <Text style={styles.tdGrupoTitle}>{tituloPeriodo(per)}</Text>
                    {grupos[per].map((it, i) => {
                        const hayOferta = (it.montoPre || 0) > it.monto;
                        return (
                            <View key={i} style={styles.tdRow}>
                                <Text style={styles.tdLabel}>{it.nombre}</Text>
                                <Text style={styles.tdValue}>
                                    {hayOferta && (
                                        <Text style={styles.ofertaPrecioTachado}>{moneyFmt(it.montoPre, currency)}{'  '}</Text>
                                    )}
                                    <Text style={hayOferta ? styles.ofertaPrecioFinal : null}>{moneyFmt(it.monto, currency)}</Text>
                                    {esGrupoRecurrente(per) && (
                                        <Text style={styles.tdPeriodo}> / {per}</Text>
                                    )}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            ))}

            {/* Subtotales por periodo — ya incluyen las ofertas aplicadas por linea */}
            {periodosOrdenados.length > 0 && (
                <View style={styles.tdBloqueSubtotales} wrap={false}>
                    {periodosOrdenados.map((per) => (
                        <View key={`st-${per}`} style={styles.tdRow}>
                            <Text style={styles.tdLabelSubtotal}>{labelSubtotal(per)}</Text>
                            <Text style={styles.tdValueSubtotal}>{moneyFmt(subtotales[per], currency)}</Text>
                        </View>
                    ))}
                    {/* Con ofertas activas se muestra la suma final (base del IGV); el descuento ya se ve tachado por línea */}
                    {descuentoTotal > 0 && (
                        <View style={styles.tdRow}>
                            <Text style={styles.tdLabelSubtotal}>Subtotal</Text>
                            <Text style={styles.tdValueSubtotal}>{moneyFmt(subtotalTotal, currency)}</Text>
                        </View>
                    )}
                </View>
            )}

            {/* IGV + TOTAL final. El TOTAL vive en un card oscuro con el color
                de marca (label pequeno arriba, monto grande abajo, nota lateral). */}
            <View style={styles.tdBloqueTotal} wrap={false}>
                {aplicaIgv && (
                    <View style={styles.tdRow}>
                        <Text style={styles.tdLabel}>IGV ({Math.round(igvRate * 100)}%)</Text>
                        <Text style={styles.tdValue}>{moneyFmt(igv, currency)}</Text>
                    </View>
                )}
                <View style={styles.tdTotalCard} wrap={false}>
                    <View style={styles.tdTotalCardLeft}>
                        <Text style={styles.tdTotalCardLabel}>TOTAL A PAGAR</Text>
                        <Text style={styles.tdTotalCardMonto}>{moneyFmt(total, currency)}</Text>
                    </View>
                    <View style={styles.tdTotalCardRight}>
                        <Text style={styles.tdTotalCardNota}>
                            Este monto incluye el primer período de todos los servicios listados.
                        </Text>
                    </View>
                </View>
            </View>

            {/* Plan de pago (uso interno) — bloque informativo con las cuotas.
                Se muestra debajo del card TOTAL A PAGAR con el detalle de cada
                cuota, ajustando el residual de redondeo en la ultima para que
                la suma coincida exactamente con `total`. */}
            {cotizacion.plan_pago && (cotizacion.plan_pago.periodos || []).length > 0 && (() => {
                const cuotas = calcularMontosPlanPago(total, cotizacion.plan_pago.periodos || []);
                return (
                    <View style={styles.tdPlanPagoWrap} wrap={false}>
                        <Text style={styles.tdPlanPagoTitulo}>Plan de pago</Text>
                        <Text style={styles.tdPlanPagoDesc}>
                            Tienes {cuotas.length} cuota{cuotas.length === 1 ? '' : 's'} para realizar los pagos. Podrás ver el detalle en cada cuota.
                        </Text>
                        <View style={styles.tdPlanPagoLista}>
                            {cuotas.map(c => (
                                <View key={c.id} style={styles.tdPlanPagoRow}>
                                    <Text style={styles.tdPlanPagoNum}>#{c.numero_periodo}</Text>
                                    <Text style={styles.tdPlanPagoNombre}>{c.nombre}</Text>
                                    <Text style={styles.tdPlanPagoPct}>{c.porcentaje}%</Text>
                                    <Text style={styles.tdPlanPagoMonto}>{moneyFmt(c.monto, currency)}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                );
            })()}

            {/* Descripcion pie sobre recurrencia — texto neutro para no confundir
                cuando hay un solo periodo o varios. El detalle del periodo ya sale
                al lado del monto (S/ 250 / anual). */}
            {periodosOrdenados.some(esGrupoRecurrente) && (
                <Text style={styles.tdNota}>
                    Los pagos recurrentes se deberán renovar según el período indicado para mantener el servicio activo.
                </Text>
            )}
        </View>
    );
};

// Tabla compacta para el bloque de opcionales — mismo layout pero tipografia
// mas chica para que se lea como bloque subordinado al total real.
const TablaOpcionalPorCategoria = ({ categoria, items, currency }) => (
    <View wrap={true}>
        <Text style={styles.opcCategoriaTitle}>{categoria}</Text>
        <View style={styles.opcTableHead} fixed={false}>
            <View style={{ width: 420 }}>
                <Text style={styles.opcTableHeadCell}>Servicio</Text>
            </View>
            <View style={{ width: 90 }}>
                <Text style={[styles.opcTableHeadCell, { textAlign: 'right' }]}>Importe</Text>
            </View>
        </View>
        {items.map((item, idx) => (
            <View key={idx} wrap={true}>
                {/* Header atomico: titulo + importe (no se separan) */}
                <View wrap={false} style={item.detalle ? styles.opcTableRowHeader : styles.opcTableRow}>
                    <View style={item.detalle ? styles.opcCellServicioHeader : styles.opcCellServicio}>
                        <Text style={styles.opcServicioNombre}>{item.nombre}</Text>
                    </View>
                    <View style={styles.opcCellImporte}>
                        {item.oferta && (
                            <Text style={styles.ofertaPrecioTachado}>
                                {moneyFmt(item.totalServicioPreOferta, currency)}
                            </Text>
                        )}
                        <Text style={item.oferta ? styles.ofertaPrecioFinal : null}>
                            {moneyFmt(item.totalServicio, currency)}
                        </Text>
                        {item.periodoNombre && (
                            <Text style={styles.servicioPeriodoSuffix}>/ {String(item.periodoNombre).toLowerCase()}</Text>
                        )}
                        {item.oferta && (
                            <Text style={styles.ofertaMotivo}>
                                {item.oferta.motivo}
                            </Text>
                        )}
                    </View>
                </View>
                {/* Body: detalle largo — puede fluir entre paginas */}
                {item.detalle && (
                    <View style={styles.opcTableRowBody}>
                        <View style={styles.opcCellServicioBody}>
                            <HtmlRenderer html={item.detalle} />
                        </View>
                        <View style={styles.opcCellImporteEmpty} />
                    </View>
                )}
                {item.costosFijos.map((cf, cfIdx) => (
                    <View key={`ocf-${cfIdx}`} style={styles.opcCostoFijoRow} wrap={false}>
                        <View style={styles.opcCostoFijoCellNombre}>
                            <Text style={styles.opcCostoFijoNombre}>{cf.nombre}</Text>
                            {cf.descripcion && (
                                <Text style={styles.opcCostoFijoDescripcion}>{cf.descripcion}</Text>
                            )}
                        </View>
                        <View style={styles.opcCellImporte}>
                            {cf.oferta && (
                                <Text style={styles.ofertaPrecioTachadoCf}>
                                    {moneyFmt(cf.subtotalPreOferta, currency)}
                                </Text>
                            )}
                            <Text style={cf.oferta ? [styles.opcCostoFijoImporte, styles.ofertaPrecioFinal] : styles.opcCostoFijoImporte}>
                                {moneyFmt(cf.subtotal, currency)}
                            </Text>
                            {cf.periodoNombre && (
                                <Text style={styles.servicioPeriodoSuffix}>/ {String(cf.periodoNombre).toLowerCase()}</Text>
                            )}
                            {cf.oferta && (
                                <Text style={styles.ofertaMotivo}>
                                    {cf.oferta.motivo}
                                </Text>
                            )}
                        </View>
                    </View>
                ))}
            </View>
        ))}
    </View>
);

// Bloque separado para servicios opcionales — al final del documento, con
// tipografia mas chica que la tabla principal para dejar claro que es un anexo.
const ServiciosOpcionales = ({ servicios, tiposServicio, currency, cotizacion, igvRate }) => {
    const opcionales = (servicios || []).filter(s => !!s.es_opcional);
    if (opcionales.length === 0) return null;

    const grupos = agruparPorCategoria(opcionales, tiposServicio);

    // Desglose por periodo — clonamos los opcionales con es_opcional=false
    // para que agruparItemsPorPeriodo no los excluya. Sin extras.
    const opcionalesParaDesglose = opcionales.map(s => ({ ...s, es_opcional: false }));
    const { grupos: gruposPeriodo, subtotales: subtotalesPeriodo, descuentoTotal: descuentoOpc } = agruparItemsPorPeriodo(opcionalesParaDesglose, []);

    const ordenPeriodo = (per) => {
        if (per === 'unico') return 0;
        if (per === 'mensual') return 1;
        if (per === 'anual') return 2;
        return 3;
    };
    const periodosOrdenados = Object.keys(gruposPeriodo).sort(
        (a, b) => ordenPeriodo(a) - ordenPeriodo(b) || a.localeCompare(b)
    );

    const subtotalOpc = Object.values(subtotalesPeriodo).reduce((a, b) => a + b, 0);
    const aplicaIgv = cotizacion.incluye_igv !== false && cotizacion.incluye_igv !== 0;
    const igvOpc = aplicaIgv ? subtotalOpc * (igvRate || 0.18) : 0;
    const totalOpcionales = subtotalOpc + igvOpc;

    return (
        <View wrap={true} style={styles.opcionalesWrap}>
            <Text style={styles.opcionalesTitle}>Opcionales</Text>
            <Text style={styles.opcionalesDesc}>
                Servicios opcionales — no están incluidos en el total de la cotización y no serán tomados en cuenta a menos que sean aprobados.
            </Text>
            {Object.entries(grupos).map(([categoria, items], idx) => (
                <TablaOpcionalPorCategoria
                    key={`opc-${idx}`}
                    categoria={categoria}
                    items={items}
                    currency={currency}
                />
            ))}

            {/* Bloque de desglose por periodo, alineado a derecha ~60%. Mismo formato
                que TotalesConDesglose pero con estilos toc* mas chicos y en muted
                para no competir con el bloque de totales principal. */}
            <View style={styles.tocWrap} wrap={false}>
                {/* Items agrupados por periodo — todos con titulo, incluido "Pago único" */}
                {periodosOrdenados.map((per) => (
                    <View key={`toc-g-${per}`} style={styles.tocGrupo}>
                        <Text style={styles.tocGrupoTitle}>{tituloPeriodo(per)}</Text>
                        {gruposPeriodo[per].map((it, i) => {
                            const hayOferta = (it.montoPre || 0) > it.monto;
                            return (
                                <View key={`toc-i-${per}-${i}`} style={styles.tocRow}>
                                    <Text style={styles.tocLabel}>{it.nombre}</Text>
                                    <Text style={styles.tocValue}>
                                        {hayOferta && (
                                            <Text style={styles.ofertaPrecioTachado}>{moneyFmt(it.montoPre, currency)}{'  '}</Text>
                                        )}
                                        <Text style={hayOferta ? styles.ofertaPrecioFinal : null}>{moneyFmt(it.monto, currency)}</Text>
                                        {per !== 'unico' && (
                                            <Text style={styles.tocPeriodo}> / {per}</Text>
                                        )}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                ))}

                {/* Subtotales por periodo — ya incluyen ofertas por linea */}
                {periodosOrdenados.length > 0 && (
                    <View style={styles.tocBloqueSubtotales}>
                        {periodosOrdenados.map((per) => (
                            <View key={`toc-st-${per}`} style={styles.tocRow}>
                                <Text style={styles.tocLabelSubtotal}>{labelSubtotal(per)}</Text>
                                <Text style={styles.tocValueSubtotal}>{moneyFmt(subtotalesPeriodo[per], currency)}</Text>
                            </View>
                        ))}
                        {descuentoOpc > 0 && (
                            <View style={styles.tocRow}>
                                <Text style={styles.tocLabelSubtotal}>Subtotal</Text>
                                <Text style={styles.tocValueSubtotal}>{moneyFmt(subtotalOpc, currency)}</Text>
                            </View>
                        )}
                    </View>
                )}

                {/* IGV + Total Opcionales */}
                <View style={styles.tocBloqueTotal}>
                    {aplicaIgv && (
                        <View style={styles.tocRow}>
                            <Text style={styles.tocLabel}>IGV opcionales ({Math.round((igvRate || 0.18) * 100)}%)</Text>
                            <Text style={styles.tocValue}>{moneyFmt(igvOpc, currency)}</Text>
                        </View>
                    )}
                    <View style={styles.tocRowTotal}>
                        <Text style={styles.tocLabelTotal}>Total Opcionales</Text>
                        <Text style={styles.tocValueTotal}>{moneyFmt(totalOpcionales, currency)}</Text>
                    </View>
                </View>

                {/* Disclaimer dinamico — mismo patron que el bloque principal. */}
                {periodosOrdenados.some(p => p !== 'unico') && (
                    <Text style={styles.tdNota}>
                        Los pagos recurrentes se deberán renovar según el período indicado para mantener el servicio activo.
                    </Text>
                )}
            </View>
        </View>
    );
};

const Totales = ({ cotizacion, igvRate, currency }) => {
    const subtotal = parseFloat(cotizacion.subtotal) || 0;
    const subtotalExtras = parseFloat(cotizacion.subtotalExtras) || 0;
    const igv = parseFloat(cotizacion.igv) || 0;
    const total = parseFloat(cotizacion.total) || 0;
    // MySQL puede devolver BOOLEAN como 0/1; interpretar default TRUE.
    const aplicaIgv = cotizacion.incluye_igv !== false && cotizacion.incluye_igv !== 0;

    return (
        <View style={styles.totalesWrap} wrap={false}>
            <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalValue}>{moneyFmt(subtotal, currency)}</Text>
            </View>
            {aplicaIgv && (
                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>IGV ({Math.round(igvRate * 100)}%)</Text>
                    <Text style={styles.totalValue}>{moneyFmt(igv, currency)}</Text>
                </View>
            )}
            {subtotalExtras > 0 && (
                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Extras</Text>
                    <Text style={styles.totalValue}>{moneyFmt(subtotalExtras, currency)}</Text>
                </View>
            )}
            <View style={[styles.totalRow, styles.totalRowBold]}>
                <Text style={styles.totalLabelBold}>TOTAL</Text>
                <Text style={styles.totalValueBold}>{moneyFmt(total, currency)}</Text>
            </View>
        </View>
    );
};

// =====================================================
// Document principal
// =====================================================
const CotizacionPDF = ({ cotizacion, servicios, extras, configuracion, tiposServicio, logoDataUrl }) => {
    const currency = cotizacion.moneda || 'PEN';
    const igvRate = parseFloat(configuracion.igv_rate) || 0.18;
    // Los opcionales no van a la tabla principal ni a Pagos Recurrentes: viven en su propio bloque al final.
    const serviciosNormales = (servicios || []).filter(s => !s.es_opcional);
    const grupos = agruparPorCategoria(serviciosNormales, tiposServicio);

    return (
        <Document
            title={`Cotización ${cotizacion.numero}`}
            author={configuracion.empresa_nombre || 'Alma Industria Creativa'}
            subject={`Cotización para ${cotizacion.cliente_nombre}`}
        >
            <Page size="A4" style={styles.page} wrap>
                {/* Footer "Pág X de N" removido — no se pide en el diseno final.
                    Para reactivar, restaurar el bloque <View fixed> con el <Text render>
                    que usaba pageNumber/totalPages. */}

                <Header
                    cotizacion={cotizacion}
                    configuracion={configuracion}
                    logoDataUrl={logoDataUrl}
                />

                {/* Servicios agrupados por categoria */}
                {Object.entries(grupos).map(([categoria, items], idx) => (
                    <TablaServiciosPorCategoria
                        key={`cat-${idx}`}
                        categoria={categoria}
                        items={items}
                        currency={currency}
                    />
                ))}

                {/* Extras */}
                <Extras extras={extras} currency={currency} />

                {/* Totales con desglose alineados a la derecha (60%).
                    El bloque de PagosRecurrentes queda comentado — su informacion
                    ya vive dentro de TotalesConDesglose (secciones "Pagos mensuales",
                    "Pagos anuales", etc). Se puede reactivar si se necesita el
                    resumen paralelo:
                    <View style={styles.totalesRowLeft}>
                        <PagosRecurrentes servicios={serviciosNormales} currency={currency} />
                    </View>
                */}
                <View style={styles.totalesRow} wrap={false}>
                    <View style={styles.totalesRowRight}>
                        <TotalesConDesglose
                            servicios={serviciosNormales}
                            extras={extras || []}
                            cotizacion={cotizacion}
                            igvRate={igvRate}
                            currency={currency}
                        />
                    </View>
                </View>

                {/* Bloque de servicios OPCIONALES — al final, con su propio total. No suma al TOTAL. */}
                <ServiciosOpcionales
                    servicios={servicios}
                    tiposServicio={tiposServicio}
                    currency={currency}
                    cotizacion={cotizacion}
                    igvRate={igvRate}
                />

                {/* Comentarios */}
                {cotizacion.comentarios && (
                    <View>
                        <Text style={styles.sectionTitle}>Comentarios:</Text>
                        <HtmlRenderer html={cotizacion.comentarios} />
                    </View>
                )}

                {/* Metodos de pago */}
                {configuracion.metodos_pago && (
                    <View>
                        <Text style={styles.sectionTitle}>Métodos de Pago:</Text>
                        <HtmlRenderer html={configuracion.metodos_pago} />
                    </View>
                )}
            </Page>
        </Document>
    );
};

export default CotizacionPDF;
